import Image from 'next/image'

import Link from 'next/link'
import EventsForm from '../EventsForm/EventsForm'

/* utils */
import { getFormatRgbaColor } from '@/utils/functions/utils'

/* assets */
import { ReactComponent as Calendar } from 'public/assets/events/calendarIcon.svg'
import { ReactComponent as Clock } from 'public/assets/events/clockIcon.svg'
import { ReactComponent as Location } from 'public/assets/events/locationIcon.svg'
import { ReactComponent as Timer } from 'public/assets/events/timerIcon.svg'

interface Props {
  heroImage: {
    data: {
      attributes: StrapiImageFile
    }
  }
  tag: string
  eventTitle: string
  eventDescription: string
  eventType: string
  eventId?: string
  venue?: string
  heroBgColor: string
  startTime: string
  endTime: string
  eventDate: string
  eventDuration: string
  slug: string
  thankYouMessage: string
  recordingUrl: string
}

function IndividualEventsPageHero({
  heroImage,
  tag,
  eventTitle,
  eventDescription,
  eventType,
  eventId,
  venue,
  heroBgColor,
  startTime,
  endTime,
  eventDate,
  eventDuration,
  slug,
  thankYouMessage,
  recordingUrl
}: Props) {
  return (
    <section className="font-body text-white relative">
      <div
        className="absolute top-0 right-0 left-0 h-[1330px] md:h-[1150px]"
        style={{ background: getFormatRgbaColor(heroBgColor) }}
      ></div>
      <div className="container | py-14 md:pt-18 md:pb-12 relative">
        <Image
          src={heroImage.data.attributes.url}
          alt={heroImage.data.attributes.alternativeText ?? ''}
          width={heroImage.data.attributes.width}
          height={heroImage.data.attributes.height}
          priority={true}
          className="border border-blue-web-60 rounded-2xl | mx-auto mb-8 md:mb-12"
        />

        <div
          className={
            'font-body font-medium | flex flex-col sm:flex-row justify-center gap-4 | w-full mb-10 md:mb-16'
          }
        >
          <Link
            href={`#events-form`}
            className={
              'new-btn-primary | font-medium | flex-grow md:max-w-[351px]'
            }
          >
            {eventType === 'Upcoming_Event'
              ? 'Register Now'
              : 'Watch Recordings'}
          </Link>
        </div>

        <div className="block md:hidden mb-2 md:mb-9">
          <span className="text-small md:text-lg font-medium md:font-bold uppercase tracking-wider border border-white | rounded-lg py-1 px-3 md:py-2 md:px-6">
            {tag}
          </span>
        </div>

        <div className="detailsSection grid grid-cols-1 md:grid-cols-12 gap-8 | mb-10 md:mb-14">
          <div className="col-span-6">
            <div className="font-heading text-3xl font-semibold | max-w-col-5 mb-5 md:mb-8">
              {eventTitle}
            </div>
            <p className="text-base md:text-lg | border-b-2 pb-8 md:pb-0 md:border-b-0 max-w-col-6">
              {eventDescription}
            </p>
          </div>
          <div className="flex justify-start md:justify-center items-center col-span-6 border-l-0 md:border-l-2">
            <div className="flex flex-col gap-6 md:gap-11 | ml-0 md:ml-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-18">
                <div>
                  <div className="flex items-center gap-2 | mb-3">
                    <Calendar />
                    <div className="font-heading text-lg md:text-xl font-bold uppercase">
                      Date
                    </div>
                  </div>
                  <span className="text-xs md:text-lg">{eventDate}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2 | mb-3">
                    <Clock />
                    <div className="font-heading text-lg md:text-xl font-bold uppercase">
                      Time
                    </div>
                  </div>
                  {
                    <span className="text-xs md:text-lg">{`${startTime} - ${endTime}`}</span>
                  }
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-18">
                <div>
                  <div className="flex items-center gap-2 | mb-3">
                    <Timer />
                    <div className="font-heading text-lg md:text-xl font-bold uppercase">
                      Duration
                    </div>
                  </div>
                  <span className="text-xs md:text-lg">{`~ ${eventDuration} mins (approx)`}</span>
                </div>
                {eventType.includes('Past') ? (
                  <div>
                    <div className="flex items-center gap-2 | mb-3">
                      <div className="font-heading text-lg md:text-xl font-bold uppercase">
                        Event Has
                      </div>
                    </div>
                    <span className="text-xs md:text-lg font-bold text-yellow-50 uppercase">
                      Ended
                    </span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 | mb-3">
                      <Location />
                      <div className="font-heading text-lg md:text-xl font-bold uppercase">
                        Venue
                      </div>
                    </div>
                    <span className="text-xs md:text-lg">{venue}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {eventType.includes('Past') ? (
          <EventsForm
            title={'Get Access To Recordings'}
            eventType={eventType}
            eventSlug={slug}
            eventId={eventId}
            thankYouMessage={thankYouMessage}
            recordingUrl={recordingUrl}
          />
        ) : (
          <EventsForm
            title={'Register Now'}
            eventType={eventType}
            venue={venue}
            eventSlug={slug}
            eventId={eventId}
            thankYouMessage={thankYouMessage}
          />
        )}
      </div>
    </section>
  )
}

export default IndividualEventsPageHero
